import { ApolloQueryResult } from '@apollo/client';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  MarketingTag,
  Portfolio,
  ProductLineAttr,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { CopyHelper } from 'global/utils/CopyHelper';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStore';
import { RootStore } from 'store/rootStore';
import { Cart } from 'types/cartService';
import { Catalog } from 'types/productService';
import ProductLineModule from 'store/modules/productLineModule';
import { getCatalog, getPortfoliosById } from 'api/operations/productServiceV2';

export type CartPageType = 'rate-segment' | 'popular' | 'browsing' | 'help-me-decide';

export class ProposalCartStore {
  rootStore: RootStore;
  productLineModule: ProductLineModule;
  loading = true;
  /**
   * Cart used to store id, or browsing criteria,
   */
  cart: Cart;
  /**
   * List of all possible tags that can be shown up on Proposal Card
   */
  marketingTags: MarketingTag[] = [];
  /**
   * List of all portfolios
   */
  portfolios: Portfolio[] = [];
  /**
   * List of all product line attributes with their titles
   */
  productLineAttrs: ProductLineAttr[] = [];
  /**
   * Show loader in current card
   */
  loaderInPortfolioId = '';

  showCrossSellModal = false;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  setShowCrossSellModal(status: boolean): void {
    this.showCrossSellModal = status;
  }

  setPortfolioIdLoader(portfolioId: string): void {
    this.loaderInPortfolioId = portfolioId;
  }

  public async collectCartData(cartId: string): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.cartStore.getCart(cartId);
    await runInAction(async () => {
      // Copy basic types to avoid possible override
      const cartData = CopyHelper.deepCopy(res.data.getCart);
      this.cart = cartData;

      const sponsorId = this.rootStore.domainStore.domainHeader.id;

      const requestsForPortfolios = [
        getCatalog(
          sponsorId,
          cartData.lastUpdateBrowsingCriteria.effectiveDate,
          cartData.rateSegment,
          cartData.lastUpdateBrowsingCriteria.state,
        ),
        getPortfoliosById(cartData.cartItems.map((item) => toJS(item)).slice()),
        this.getProductLines(cartData),
      ];

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const promisesResult = await Promise.all(requestsForPortfolios);
      void runInAction(() => {
        const catalog = (promisesResult[0] as ApolloQueryResult<{ getCatalog: Catalog }>).data.getCatalog;
        const portfolios = promisesResult[1] as Portfolio[];

        const sortedPortfolios = portfolios
          .map((portfolio) => toJS(portfolio))
          .slice()
          .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

        this.portfolios = sortedPortfolios;
        this.productLineAttrs = catalog.productLineAttributes;
        this.marketingTags = catalog.marketingTags;
        // Show a link to the shopping cart in the header and provide the necessary data for redirection
        this.rootStore.domainStore.setDataForCartRedirect(cartId, cartData.groupId, true);
        this.rootStore.domainStore.changeCartItemsQuantity(cartData.cartItems.length);
        this.setLoading(false);
      });
    });
  }

  private async getProductLines(cart: Cart): Promise<void> {
    await this.productLineModule.getProductLines(cart, this.rootStore.domainStore.domainHeader.id);
    runInAction(() => {
      if (
        cart.cartItems.length > 0 &&
        this.productLineModule.getUnusedProductLineIds(this.addedProductLineIdsToCart).length > 0
      )
        this.setShowCrossSellModal(true);
    });
  }

  public setBrowsingCriteria(id: string, selectedBcIndex: number): void {
    const currentCartItem = this.cart.cartItems.find(({ portfolioId }) => id === portfolioId);
    if (currentCartItem) {
      currentCartItem.selectedIndex = selectedBcIndex;
    }
  }

  public getPortfolioById(portfolioId: string): Portfolio | undefined {
    return this.portfolios.find((portfolio) => portfolio.id === portfolioId);
  }

  onViewPlanDetails(portfolioId: string, cartType: string, pushFn: (link: string) => void): void {
    const productLineId = this.getPortfolioById(portfolioId)?.productLineId;
    pushFn(
      `/product-compare?type=proposal-details&portfolioId=${portfolioId}&cartId=${
        this.cart.id
      }&productLineId=${productLineId!}&groupId=${this.cart.groupId}&cartType=${cartType}`,
    );
  }

  async onRemoveFromCart(portfolioId: string): Promise<void> {
    this.setPortfolioIdLoader(portfolioId);
    // isLast === false -> remove cart item with all bc
    const res = await this.rootStore.cartStore.removePortfolioFromCart(this.cart.id, portfolioId, false);
    runInAction(() => {
      const currentCart = res.data?.removePortfolioConfigFromCart;
      if (currentCart) {
        this.cart = currentCart;
        this.rootStore.domainStore.changeCartItemsQuantity(currentCart.cartItems.length);
      }
      this.setPortfolioIdLoader('');
    });
  }

  public get addedProductLineIdsToCart(): ProductLineId[] {
    return [...new Set(this.cart.cartItems.map((cartItem) => cartItem.productLineId))];
  }

  public checkCarrierProductLinesRules(): Record<string, string>[] {
    return this.productLineModule.checkCarrierProductLinesRules(this.addedProductLineIdsToCart);
  }

  public checkUnusedProductLineIds(): ProductLineId[] {
    return this.productLineModule.getUnusedProductLineIds(this.addedProductLineIdsToCart);
  }

  resetInitialData(): void {
    this.cart = INITIAL_CART_OBJECT;
    this.marketingTags = [];
    this.portfolios = [];
    this.productLineAttrs = [];
    this.showCrossSellModal = false;
    this.productLineModule = new ProductLineModule();
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.cart = INITIAL_CART_OBJECT;
    this.productLineModule = new ProductLineModule();

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
